import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, Maximize2 } from 'lucide-react';
import HRReportsTab from './HRReportsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// tone by completion %
const tone = (pct) => {
  if (pct >= 75) return { bar: 'bg-emerald-500', text: 'text-emerald-600' };
  if (pct >= 40) return { bar: 'bg-amber-500', text: 'text-amber-600' };
  return { bar: 'bg-red-500', text: 'text-red-600' };
};

const MetricTile = ({ label, pct, detail, testid }) => {
  const noData = pct === null || pct === undefined;
  const t = tone(pct || 0);
  return (
    <div className="p-4 border rounded-lg hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-start justify-between mb-2 gap-2">
        <p className="text-sm font-semibold text-stone-700 leading-tight">{label}</p>
        <p className={`text-lg font-extrabold leading-none ${noData ? 'text-stone-400' : t.text}`}>
          {noData ? '—' : `${pct}%`}
        </p>
      </div>
      <div className="h-2 w-full bg-stone-100 rounded-full overflow-hidden">
        {!noData && (
          <div className={`h-full ${t.bar} rounded-full transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
        )}
      </div>
      <p className="text-[11px] text-stone-400 mt-1.5 truncate">{detail}</p>
    </div>
  );
};

const CMDHRReportsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillOpen, setDrillOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('crm_token');
        const res = await axios.get(`${API}/hr/reports/dashboard-overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card data-testid="cmd-hr-reports-loading">
        <CardContent className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const cards = data.cards || [];
  const hr = data.hrReports || {};
  const ot = data.otherTabs || {};

  const metrics = [
    ...cards.map((c) => ({
      key: c.key,
      label: c.label,
      pct: c.total === 0 ? null : c.pct,
      detail: c.total === 0 ? 'No data' : `${c.done.toLocaleString()} of ${c.total.toLocaleString()} done`,
    })),
    { key: 'performance', label: 'Performance', pct: hr.performancePct ?? null, detail: 'Employees rated Done' },
    { key: 'skills', label: 'Skills', pct: hr.skillPct ?? null, detail: 'Skill checklist done' },
    { key: 'documents', label: 'Document Compliance', pct: hr.documentPct ?? null, detail: 'Setup docs OK' },
    { key: 'staff-advance', label: 'Staff Advance', pct: ot.staffAdvancePct ?? null, detail: `${ot.staffAdvanceFilled || 0} cells filled` },
  ];

  return (
    <Card data-testid="cmd-hr-reports-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            HR Department — Report Completion
          </CardTitle>
          <div className="flex items-center gap-4">
            <Button size="sm" variant="outline"
              className="h-9 border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => setDrillOpen(true)} data-testid="cmd-hr-open-full-reports">
              <Maximize2 className="w-4 h-4 mr-1.5" /> Open Full Reports
            </Button>
            <div className="text-right">
              <p className="text-2xl font-extrabold text-purple-700 leading-none" data-testid="cmd-hr-overall-pct">{data.overallPct}%</p>
              <p className="text-[11px] text-stone-500 uppercase tracking-wide">Overall Completion</p>
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-2xl font-extrabold text-stone-800 leading-none">{Number(data.totalStaff || 0).toLocaleString()}</p>
              <p className="text-[11px] text-stone-500 uppercase tracking-wide">Total Staff</p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {metrics.map((m) => (
            <button key={m.key} type="button" onClick={() => setDrillOpen(true)}
              className="text-left focus:outline-none focus:ring-2 focus:ring-purple-300 rounded-lg"
              data-testid={`cmd-hr-metric-btn-${m.key}`}>
              <MetricTile testid={`cmd-hr-metric-${m.key}`} label={m.label} pct={m.pct} detail={m.detail} />
            </button>
          ))}
        </div>
        <p className="text-[11px] text-stone-400 mt-3">Tip: click any metric to open the full HR reports.</p>
      </CardContent>

      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="cmd-hr-reports-drilldown">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Users className="w-5 h-5" /> HR Department — Full Reports
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <HRReportsTab />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CMDHRReportsPanel;
